import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useToast } from "../../hooks/use-toast";
import { Loader2, Sparkles, Trash2, Eye, EyeOff, PenSquare } from "lucide-react";

const AdminBlog = () => {
  const { toast } = useToast();
  const [posts, setPosts] = useState(null);
  const [topic, setTopic] = useState("");
  const [keywords, setKeywords] = useState("");
  const [tag, setTag] = useState("Guides");
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = () => api.adminBlog().then(setPosts).catch(() => setPosts([]));
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!topic.trim()) { toast({ title: "Enter a topic", variant: "destructive" }); return; }
    setGenerating(true);
    try {
      const post = await api.generatePost({ topic, keywords, tag });
      setPreview(post);
      setTopic(""); setKeywords("");
      load();
      toast({ title: "Draft generated!", description: "Review it below and publish when ready." });
    } catch (e) {
      toast({ title: "Generation failed", description: "Please try again.", variant: "destructive" });
    } finally { setGenerating(false); }
  };

  const togglePublish = async (p) => {
    await api.updatePost(p.id, { title: p.title, tag: p.tag, author: p.author, excerpt: p.excerpt, meta_description: p.meta_description, cover: p.cover, body: p.body, published: !p.published });
    load();
  };
  const remove = async (id) => { await api.deletePost(id); load(); if (preview?.id === id) setPreview(null); };

  return (
    <div>
      <h1 className="font-display text-3xl text-white mb-1 flex items-center gap-2"><Sparkles className="h-7 w-7 text-[#f5a524]" /> AI Blogger</h1>
      <p className="text-slate-400 text-sm mb-6">Generate SEO blog posts for San Juan del Sur fishing. Drafts are unpublished until you review them.</p>

      <div className="rounded-2xl border border-[#f5a524]/30 bg-[#0e2740] p-6 mb-8">
        <div className="grid md:grid-cols-2 gap-4">
          <div><Label className="text-slate-300">Topic / title idea</Label><Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. How to catch marlin in Nicaragua" className="mt-1 bg-white/5 border-white/15 text-white" /></div>
          <div><Label className="text-slate-300">Target keywords</Label><Input value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="marlin fishing san juan del sur" className="mt-1 bg-white/5 border-white/15 text-white" /></div>
        </div>
        <div className="flex items-end gap-3 mt-4">
          <div><Label className="text-slate-300">Category</Label>
            <select value={tag} onChange={(e) => setTag(e.target.value)} className="mt-1 h-10 rounded-md bg-white/5 border border-white/15 px-3 text-sm text-white">
              {["Guides","Species","Seasons","Destinations","Tips"].map((t) => (<option key={t} className="text-black">{t}</option>))}
            </select>
          </div>
          <Button onClick={generate} disabled={generating} className="btn-gold h-10 gap-2">{generating ? <><Loader2 className="h-4 w-4 animate-spin" /> Writing…</> : <><Sparkles className="h-4 w-4" /> Generate with AI</>}</Button>
        </div>
      </div>

      {preview && (
        <div className="rounded-2xl border border-[#0e9488]/40 bg-[#0e2740] p-6 mb-8">
          <p className="text-xs text-[#0e9488] font-heading uppercase tracking-wide mb-1">Freshly generated draft</p>
          <h2 className="font-display text-2xl text-white">{preview.title}</h2>
          <p className="text-slate-400 text-sm mt-1">{preview.excerpt}</p>
          <div className="mt-3 space-y-2 max-h-52 overflow-y-auto pr-2">{preview.body.map((p, i) => (<p key={i} className="text-sm text-slate-300">{p}</p>))}</div>
          <Button onClick={() => togglePublish(preview)} className="btn-gold mt-4 gap-2"><Eye className="h-4 w-4" /> Publish now</Button>
        </div>
      )}

      <h3 className="font-heading text-xl text-white mb-3">All posts</h3>
      {posts === null ? (<div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#f5a524]" /></div>) : (
        <div className="space-y-3">
          {posts.map((p) => (
            <div key={p.id} className="rounded-xl border border-white/10 bg-[#0e2740] p-4 flex items-center gap-4">
              <img src={p.cover} alt="" className="h-14 w-14 rounded-lg object-cover hidden sm:block" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><span className="font-heading text-white truncate">{p.title}</span><span className={`rounded-full px-2 py-0.5 text-[10px] ${p.published ? "bg-[#0e9488] text-white" : "bg-white/10 text-slate-400"}`}>{p.published ? "Published" : "Draft"}</span></div>
                <p className="text-xs text-slate-400 truncate">{p.excerpt}</p>
              </div>
              <button onClick={() => togglePublish(p)} className="rounded-lg bg-white/5 text-slate-300 p-2.5 hover:bg-white/10" title={p.published ? "Unpublish" : "Publish"}>{p.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
              <button onClick={() => remove(p.id)} className="rounded-lg bg-red-500/15 text-red-400 p-2.5 hover:bg-red-500/30"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {posts.length === 0 && <div className="text-center py-12 text-slate-500"><PenSquare className="h-8 w-8 mx-auto mb-2 opacity-40" />No posts yet.</div>}
        </div>
      )}
    </div>
  );
};

export default AdminBlog;
